<script setup lang="ts">
const { t: $t } = useI18n()

import { addYears, startOfToday } from 'date-fns'
import type { MachineryUVVReportInspectionType } from '@prisma/client'
import type { MachineryCategories, UpsertUVVReportInput } from '~/types'

const props = defineProps<{ payload: { machineryId: string, id?: string, readOnly?: boolean, isForeignMachinery?: boolean } }>()
const emit = defineEmits(['close'])

const { username } = useAuthorization()
const mode = computed(() => (props.payload.id ? 'update' : 'create'))
const { machineryUVVReport, machinery: machineryQuery } = useQuery()
const { data: machinery, isLoading: isLoadingMachinery, isFetching: isFetchingMachinery } = machineryQuery.machineryWithBaseInfoById(
  props.payload.machineryId,
  props.payload.isForeignMachinery,
)
const { data, isLoading: isLoadingReport, isFetching: isFetchingUVVReport } = machineryUVVReport.byId(props.payload.id)

const machineryCategory = computed(() => {
  if (!machinery.value) {
    return undefined
  }
  return machinery.value.category as MachineryCategories
})

const payload = computed((): UpsertUVVReportInput | undefined => {
  if (isFetchingMachinery.value || !machineryCategory.value || !machinery.value) {
    return undefined
  }

  const nextUVVRequiredDate = machinery.value.nextUVVRequiredDate ?? addYears(startOfToday(), 1)
  const operatingHours = machinery.value.receptionMachinery?.operatingHours ?? undefined
  if (mode.value === 'create') {
    return {
      machineryId: props.payload.machineryId,
      inspectionDate: startOfToday(),
      inspectionItems: createInspectionItems(machineryCategory.value),
      inspectionType: machinery.value.category === 'platform' ? 'PeriodicInspection' satisfies MachineryUVVReportInspectionType : undefined,
      signature: '',
      signatoryName: username.value ?? '',
      photos: [],
      nextUVVRequiredDate,
      operatingHours,
    }
  }
  if (data.value && !isFetchingUVVReport.value) {
    return {
      ...data.value,
      nextUVVRequiredDate,
      operatingHours,
    }
  }
})

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const upsert = useMutation({
  mutationFn: $trpc.machineryUVVReport.upsert.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t(`machineryUVVReport.notification.${mode.value}.error`) }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['machineryUVVReport'] }),
      queryClient.invalidateQueries({ queryKey: ['machines'] }),
    ])
    notification.success({ title: $t(`machineryUVVReport.notification.${mode.value}.success`), duration: 4500 })
    emit('close')
  },
})

/** Table */
const i18n = useI18n()
const machineryColumn = computed(() => getMachineryUVVReportColumns(i18n.t).machineryColumn)

// utils
function createInspectionItems(machineryCategory: MachineryCategories) {
  const config = machineryUVVReportConfigByMachineryCategories[machineryCategory]
  if (!config) {
    return []
  }
  return Object.values(config.items).flat().map(({ type }) => {
    return ({
      type,
      isSkipped: false,
    })
  })
}
</script>

<template>
  <ThePopup
    show
    width="900px"
    :title="$t(`machineryUVVReport.${mode}.title`)"
    @close="emit('close')"
  >
    <n-skeleton
      v-if="isLoadingMachinery || isLoadingReport"
      height="40px"
      round
    />
    <template v-else-if="payload && machineryCategory">
      <TableOneDataColumn class="mb-4" :config="{ columns: machineryColumn, data: machinery }" />

      <MachineryUVVReportCreateOrEditForm
        :payload
        :read-only="props.payload.readOnly"
        :machinery-category
        @save="upsert.mutateAsync"
      />
    </template>
  </ThePopup>
</template>
